import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Siren, ShieldAlert, MapPin, Clock } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { useSettings } from "../context/SettingsContext";
import { SERVICES } from "../data/services";
import { ServiceCard } from "../components/site/ServiceCard";
import { CallButton, TextButton, PhoneSoonNote } from "../components/site/CallButton";
import { useSeo } from "../components/site/Seo";

const Hero = () => {
  const { t } = useLang();
  const { settings } = useSettings();
  return (
    <section className="relative isolate overflow-hidden grain" aria-labelledby="hero-title">
      <img src="/images/hero.webp" alt={t("hero.imageAlt")} className="absolute inset-0 -z-10 h-full w-full object-cover object-center" fetchpriority="high" />
      <div className="absolute inset-0 -z-10 bg-[#0A0A0C]/75 sm:bg-transparent sm:bg-[linear-gradient(90deg,#0A0A0C_0%,rgba(10,10,12,.88)_45%,rgba(10,10,12,.35)_100%)]" />
      <div className="container-x py-16 sm:py-24 lg:py-32">
        <div className="max-w-3xl">
          <p className="rise inline-flex items-center gap-2 rounded-full border border-[#DC2626]/50 bg-[#DC2626]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-red-300">
            <span className="h-2 w-2 rounded-full bg-[#DC2626] beacon" aria-hidden="true" /> {t("hero.eyebrow")}
          </p>
          <h1 id="hero-title" data-testid="hero-headline" className="rise mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[0.95] tracking-tight text-white" style={{ animationDelay: "80ms" }}>
            {t("hero.title")}
          </h1>
          <p data-testid="hero-subtext" className="rise mt-5 text-lg sm:text-xl font-semibold text-[#FACC15]" style={{ animationDelay: "160ms" }}>
            {settings.is_24_7 ? t("hero.sub247") : t("hero.sub")}
          </p>
          <p className="rise mt-3 max-w-xl text-base text-gray-300" style={{ animationDelay: "220ms" }}>{t("hero.note")}</p>
          <div className="rise mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3" style={{ animationDelay: "280ms" }}>
            <Link to="/request" data-testid="hero-get-help-btn" className="btn-red px-8 py-4 text-base">
              <Siren className="h-5 w-5" aria-hidden="true" /> {t("cta.getHelp")}
            </Link>
            <CallButton testId="hero-call-now-btn" className="px-8 py-4 text-base" showNumber />
            <TextButton testId="hero-text-us-btn" className="px-6 py-4 text-base" />
          </div>
          <div className="mt-3"><PhoneSoonNote testId="hero-phone-soon" /></div>
        </div>
      </div>
      <div className="hazard-stripe h-2 w-full" aria-hidden="true" />
    </section>
  );
};

const ServicesSection = () => {
  const { t } = useLang();
  const { settings } = useSettings();
  const list = SERVICES.filter((s) => settings.enabled_services.includes(s.id));
  return (
    <section id="services" className="container-x py-20 sm:py-28 scroll-mt-20" aria-labelledby="services-title">
      <div className="max-w-2xl">
        <h2 id="services-title" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight">{t("sections.servicesTitle")}</h2>
        <p className="mt-3 text-gray-400">{t("sections.servicesSub")}</p>
      </div>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="services-grid">
        {list.map((s, i) => <ServiceCard key={s.id} service={s} index={i} />)}
      </div>
    </section>
  );
};

const HowSection = () => {
  const { t } = useLang();
  const steps = t("sections.how");
  return (
    <section id="how" className="border-y border-[#2E2E3A] bg-[#121216] scroll-mt-20" aria-labelledby="how-title">
      <div className="container-x py-20 sm:py-24 grid gap-12 lg:grid-cols-[1fr_2fr]">
        <div>
          <h2 id="how-title" className="text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">{t("sections.howTitle")}</h2>
          <img src="/images/roadside.webp" alt="Roadside technician changing a flat tire on an SUV at dusk with hazard lights on" className="mt-8 hidden lg:block w-full rounded-xl border border-[#2E2E3A] object-cover aspect-[4/3]" loading="lazy" />
        </div>
        <ol className="grid gap-5 sm:grid-cols-2">
          {steps.map(([title, text], i) => (
            <li key={title} className="panel p-6">
              <span className="font-display text-5xl font-extrabold text-[#DC2626]">0{i + 1}</span>
              <h3 className="mt-3 text-xl font-bold uppercase">{title}</h3>
              <p className="mt-2 text-sm text-gray-400">{text}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

const AreaSection = () => {
  const { t } = useLang();
  const { settings } = useSettings();
  return (
    <section id="area" className="container-x py-20 sm:py-24 grid gap-6 lg:grid-cols-3 scroll-mt-20" aria-labelledby="area-title">
      <div className="panel p-7 lg:col-span-2">
        <MapPin className="h-8 w-8 text-[#FACC15]" aria-hidden="true" />
        <h2 id="area-title" className="mt-4 text-3xl font-extrabold uppercase">{t("sections.areaTitle")}</h2>
        <p className="mt-3 text-gray-300" data-testid="service-area-text">{t("sections.areaSub", { area: settings.service_area_text || "Houston, Texas" })}</p>
        {settings.service_zips?.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400">{t("sections.zips")}</h3>
            <ul className="mt-3 flex flex-wrap gap-2" data-testid="service-zips">
              {settings.service_zips.map((z) => <li key={z} className="rounded-full border border-[#3F3F4E] px-3 py-1 font-mono text-sm">{z}</li>)}
            </ul>
          </div>
        )}
        {(settings.hours_text || settings.is_24_7) && (
          <p className="mt-6 flex items-center gap-2 text-gray-300" data-testid="business-hours">
            <Clock className="h-5 w-5 text-[#FACC15]" aria-hidden="true" />
            <span className="font-semibold">{t("sections.hours")}:</span> {settings.is_24_7 ? "24/7" : settings.hours_text}
          </p>
        )}
      </div>
      <div className="rounded-xl border border-[#DC2626]/50 bg-[#DC2626]/10 p-7">
        <ShieldAlert className="h-8 w-8 text-[#DC2626]" aria-hidden="true" />
        <h2 className="mt-4 text-2xl font-extrabold uppercase">{t("sections.safetyTitle")}</h2>
        <p className="mt-3 text-sm text-gray-200">{t("sections.safetyText")}</p>
        <a href="tel:911" className="mt-5 inline-flex font-bold text-white underline underline-offset-4" data-testid="safety-call-911">911</a>
      </div>
    </section>
  );
};

const PricingSection = () => {
  const { t } = useLang();
  const { settings } = useSettings();
  if (!settings.show_prices) return null;
  const prices = Object.entries(settings.service_prices || {}).filter(([id]) => settings.enabled_services.includes(id));
  const money = (n) => `$${Number(n).toFixed(2)}`;
  return (
    <section className="container-x pb-20" aria-labelledby="pricing-title" data-testid="pricing-section">
      <div className="panel p-7">
        <h2 id="pricing-title" className="text-3xl font-extrabold uppercase">{t("sections.pricingTitle")}</h2>
        <dl className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {settings.service_call_fee != null && <div><dt className="text-sm text-gray-400">{t("sections.serviceCall")}</dt><dd className="text-2xl font-bold text-[#FACC15]">{money(settings.service_call_fee)}</dd></div>}
          {settings.mileage_rate != null && <div><dt className="text-sm text-gray-400">{t("sections.perMile")}</dt><dd className="text-2xl font-bold text-[#FACC15]">{money(settings.mileage_rate)}</dd></div>}
          {prices.map(([id, p]) => <div key={id}><dt className="text-sm text-gray-400">{t(`services.${id}`)[0]}</dt><dd className="text-xl font-bold">{t("sections.from")} {money(p)}</dd></div>)}
        </dl>
        <p className="mt-6 text-sm text-gray-400">{settings.pricing_note || t("sections.customQuote")}</p>
      </div>
    </section>
  );
};

export const CtaBand = () => {
  const { t } = useLang();
  return (
    <section className="bg-[#DC2626]">
      <div className="container-x py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div>
          <h2 className="text-3xl sm:text-4xl font-extrabold uppercase text-white">{t("sections.ctaTitle")}</h2>
          <p className="mt-2 text-red-100">{t("sections.ctaSub")}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/request" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0A0C] px-8 py-4 font-bold uppercase text-white hover:bg-black transition-colors" data-testid="cta-band-get-help-btn">
            <Siren className="h-5 w-5" aria-hidden="true" /> {t("cta.getHelp")}
          </Link>
          <CallButton testId="cta-band-call-btn" className="px-8 py-4" />
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const { hash } = useLocation();
  useSeo(
    lang === "es" ? "Servicio de Grúa y Asistencia en Carretera en Houston, TX" : "Towing & Roadside Assistance in Houston, TX | Tow Truck & Roadside Assistance",
    `${settings.is_24_7 ? t("hero.sub247") : t("hero.sub")} ${t("hero.note")}`
  );
  useEffect(() => {
    if (hash) document.querySelector(hash)?.scrollIntoView();
  }, [hash]);
  return (
    <>
      <Hero />
      <ServicesSection />
      <HowSection />
      <AreaSection />
      <PricingSection />
      <CtaBand />
    </>
  );
}
